/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import api from 'api';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import { useEffect, useState, useRef } from 'react';
import { LTHeader, LTItem, LTRow, LTPlaceholder, LTMobileItem } from 'components/common/ListTable';
import { CustomLTRow } from '../../../Dashboards-B2B/components/customLTRow/customLTRow';
import Skeleton from '@mui/material/Skeleton';
import { loadApp } from 'redux/application/application.middleware';
import { setActiveApp } from 'redux/application/application.slice';
import { useAuth } from 'contexts/ProtectedRoute';
import { AxiosError } from 'axios';
import * as S from '../../../Dashboards-B2B/components/Table.styled';
import AlertMessage, { AlertObj } from 'components/common/alert';
import ForwardToInboxRoundedIcon from '@mui/icons-material/ForwardToInboxRounded';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { FormControl, IconButton, MenuItem, Select } from '@mui/material';
import Button from 'views/Dashboards-B2B/components/Button';
import { useTranslation } from 'react-i18next';
import { AdminUsers } from 'api/models/response/AdminUsers';
import PermissionsModal from './PermissionsModal';
import EditUserProfileModal from './EditUserProfileModal';
import { canEditUserProfile, canEditUserRole, getAssignableRoles } from 'data/constants/roles';

type AdminTableProps = {
    adminUsers: AdminUsers[];
    adminUsersForTable: AdminUsers[];
    setAdminUsersForTable: (apps: AdminUsers[]) => void;
    isAdminUsersLoading: boolean;
    reloadClients: () => void;
    mobile: boolean;
};

const AdminTable = ({
    adminUsers,
    adminUsersForTable,
    setAdminUsersForTable,
    isAdminUsersLoading,
    reloadClients,
    mobile,
}: AdminTableProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const tableRef = useRef<HTMLDivElement | null>(null);
    const [selectedApps, setSelectedApps] = useState<number[]>([]);
    const skeletonArray = adminUsersForTable.length > 0 ? adminUsersForTable : [1, 2, 3];
    const { role } = useAuth();

    useEffect(() => {
        dispatch(setActiveApp(null));
        //eslint-disable-next-line
    }, []);

    useEffect(() => {
        localStorage.setItem('selectedApps', JSON.stringify(selectedApps));
    }, [selectedApps]);

    useEffect(() => {
        //this is needed to reset the select app checkboxes after assigning clients
        setSelectedApps([]);
    }, [adminUsers]);

    useEffect(() => {
        setAdminUsersForTable(adminUsers);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminUsers]);

    const [isShowingAlert, setShowingAlert] = useState(false);
    const [selectedUserForPermissions, setSelectedUserForPermissions] = useState<AdminUsers | null>(
        null
    );
    const [selectedUserForEdit, setSelectedUserForEdit] = useState<AdminUsers | null>(null);
    const [updatingRoleUserId, setUpdatingRoleUserId] = useState<number | null>(null);

    const [alertMessage, setAlertMessage] = useState<AlertObj>({ type: 'info', value: '' });

    // The old admin action emailed the user a freshly generated plaintext
    // password. Security migration: it now triggers the same one-time,
    // expiring reset link as the public "Forgot password" flow via
    // POST /api/forgot_password, so the user sets their own password on
    // /reset-password. The endpoint is intentionally non-enumerating and
    // rate limited (5/hour per IP), which we surface on 429.
    const handleSendResetLink = async (user: AdminUsers) => {
        if (!user?.email) {
            setAlertMessage({ type: 'error', value: t('somethingWentWrong', { lng: language }) });
            setShowingAlert(true);
            return;
        }
        try {
            await api.forgotPassword.post({ email: user.email.trim().toLowerCase() });
            setAlertMessage({
                type: 'success',
                value: t('resetLinkSentToUser', { lng: language }),
            });
            setShowingAlert(true);
        } catch (error) {
            const status = (error as AxiosError)?.response?.status;
            setAlertMessage({
                type: 'error',
                value:
                    status === 429
                        ? t('tooManyRequests', { lng: language })
                        : t('somethingWentWrong', { lng: language }),
            });
            setShowingAlert(true);
        }
    };

    const handleRoleChange = async (user: AdminUsers, nextRole: string) => {
        if (!nextRole || nextRole === user.role) return;
        setUpdatingRoleUserId(user.id);
        try {
            await api.immigrationProfessional.put(user.id, { role: nextRole } as any);
            setAlertMessage({
                type: 'success',
                value: t('roleUpdatedSuccessfully', {
                    lng: language,
                    defaultValue: 'Role updated successfully.',
                }),
            });
            setShowingAlert(true);
            reloadClients();
        } catch {
            setAlertMessage({
                type: 'error',
                value: t('somethingWentWrong', { lng: language }),
            });
            setShowingAlert(true);
        } finally {
            setUpdatingRoleUserId(null);
        }
    };

    const handleResendInvitation = async (userId: number) => {
        try {
            const request = await api.sendInvitation.sendInvitation(userId);
            if (request.status === 200 || request.status === 201) {
                setAlertMessage({
                    type: 'success',
                    value: t('emailSuccessfullySent', { lng: language }),
                });
                setShowingAlert(true);
            }
        } catch {
            setAlertMessage({
                type: 'error',
                value: t('somethingWentWrong', { lng: language }),
            });
            setShowingAlert(true);
        }
    };

    const assignableRoles = getAssignableRoles(role);

    const formatRoleLabel = (value: string) =>
        value
            ? value
                  .toLowerCase()
                  .split('_')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')
            : '';

    const Alert = (
        <AlertMessage
            type={alertMessage?.type}
            text={alertMessage?.value}
            open={isShowingAlert}
            onClose={() => setShowingAlert(false)}
        ></AlertMessage>
    );
    return (
        <>
            {Alert}
            <PermissionsModal
                open={Boolean(selectedUserForPermissions)}
                user={selectedUserForPermissions}
                onClose={() => setSelectedUserForPermissions(null)}
            />
            <EditUserProfileModal
                open={Boolean(selectedUserForEdit)}
                user={selectedUserForEdit}
                onClose={() => setSelectedUserForEdit(null)}
                onSaved={() => {
                    reloadClients();
                }}
            />
            <S.ListTable
                ref={tableRef}
                style={
                    mobile
                        ? { maxHeight: 'unset', width: '100%', maxWidth: '100%' }
                        : { maxHeight: 'unset', width: '100%', maxWidth: '100%', zIndex: 1 }
                }
            >
                <LTHeader style={{ paddingLeft: '16px' }}>
                    {/* tooltips are present in LTItem itself */}
                    <LTItem
                        tip={t('clientId', { lng: language })}
                        maxWidth="60px"
                        minWidth="60px"
                        style={{
                            textAlign: 'center',
                            whiteSpace: 'normal',
                            marginRight: '1rem',
                            lineHeight: '1.2',
                        }}
                    >
                        {t('clientId', { lng: language })}
                    </LTItem>
                    <LTItem maxWidth="130px" minWidth="130px" style={{ marginRight: '1rem' }}>
                        {t('name', { lng: language })}
                    </LTItem>
                    <LTItem maxWidth="150px" minWidth="150px" style={{ marginRight: '1rem' }}>
                        {t('email', { lng: language })}
                    </LTItem>
                    <LTItem maxWidth="150px" minWidth="150px" style={{ marginRight: '1rem' }}>
                        {t('company', { lng: language })}
                    </LTItem>
                    <LTItem maxWidth="170px" minWidth="170px" style={{ marginRight: '1rem' }}>
                        {t('role', { lng: language, defaultValue: 'Role' })}
                    </LTItem>
                    <LTItem maxWidth="150px" minWidth="150px" style={{ marginRight: '1rem' }}>
                        {t('status', { lng: language, defaultValue: 'Status' })}
                    </LTItem>
                    <LTItem maxWidth="150px" minWidth="150px" style={{ marginRight: '1rem' }}>
                        {t('sendResetLink', { lng: language })}
                    </LTItem>
                    <LTItem maxWidth="120px" minWidth="120px" style={{ marginRight: '1rem' }}>
                        {t('permissions', { lng: language })}
                    </LTItem>
                    {/* <LTItem maxWidth="150px" minWidth="150px" style={{ marginRight: '1rem' }}>
                        {t('eligibility', { lng: language })}
                    </LTItem> */}
                </LTHeader>

                {!isAdminUsersLoading && adminUsersForTable.length === 0 && (
                    <LTPlaceholder>{t('theListIsEmpty', { lng: language })}</LTPlaceholder>
                )}

                {!isAdminUsersLoading && adminUsersForTable.length > 0 && (
                    <>
                        {adminUsersForTable.map((clientApp) => (
                            <CustomLTRow
                                key={`client_${clientApp.id}`}
                                id={`client_${clientApp.id}`}
                                style={{ paddingLeft: '16px' }}
                            >
                                <LTItem
                                    maxWidth="60px"
                                    minWidth="60px"
                                    style={{ textAlign: 'center', marginRight: '1rem' }}
                                >
                                    {clientApp.id}
                                </LTItem>
                                <LTItem
                                    maxWidth="130px"
                                    minWidth="130px"
                                    tip={`${clientApp.firstname} ${clientApp.lastname}`}
                                    style={{ marginRight: '1rem' }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            minWidth: 0,
                                        }}
                                    >
                                        {canEditUserProfile(role, clientApp.role) && (
                                            <IconButton
                                                size="small"
                                                title={t('editUser', {
                                                    lng: language,
                                                    defaultValue: 'Edit user',
                                                })}
                                                onClick={() => setSelectedUserForEdit(clientApp)}
                                                sx={{
                                                    padding: '2px',
                                                    color: '#6d41d8',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(131, 83, 226, 0.08)',
                                                    },
                                                }}
                                            >
                                                <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        )}
                                        <S.Link
                                            onClick={() =>
                                                dispatch(loadApp(clientApp.id.toString()))
                                            }
                                            to={`/dashboard/${clientApp.id}/client-profile`}
                                        >
                                            {`${clientApp.firstname} ${clientApp.lastname}`}
                                        </S.Link>
                                    </div>
                                </LTItem>

                                <LTItem
                                    maxWidth="150px"
                                    minWidth="150px"
                                    style={{ marginRight: '1rem' }}
                                >
                                    {clientApp.email}
                                </LTItem>
                                <LTItem
                                    maxWidth="150px"
                                    minWidth="150px"
                                    style={{ marginRight: '1rem' }}
                                >
                                    {clientApp.company_name?.replace(/[[\]]/g, '')}
                                </LTItem>

                                <LTItem
                                    maxWidth="170px"
                                    minWidth="170px"
                                    style={{ marginRight: '1rem' }}
                                >
                                    {(() => {
                                        const canEdit = canEditUserRole(role, clientApp.role);
                                        const options = Array.from(
                                            new Set([
                                                ...assignableRoles,
                                                ...(clientApp.role ? [clientApp.role] : []),
                                            ])
                                        );
                                        if (!canEdit) {
                                            return (
                                                <span
                                                    style={{ color: '#475569', fontSize: '13px' }}
                                                >
                                                    {clientApp.role
                                                        ? formatRoleLabel(clientApp.role)
                                                        : '—'}
                                                </span>
                                            );
                                        }
                                        return (
                                            <FormControl
                                                size="small"
                                                fullWidth
                                                disabled={updatingRoleUserId === clientApp.id}
                                            >
                                                <Select
                                                    value={clientApp.role ?? ''}
                                                    onChange={(e) =>
                                                        handleRoleChange(
                                                            clientApp,
                                                            e.target.value as string
                                                        )
                                                    }
                                                    displayEmpty
                                                    sx={{
                                                        fontSize: '13px',
                                                        backgroundColor: '#ffffff',
                                                        borderRadius: '10px',
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor:
                                                                'rgba(148, 163, 184, 0.35)',
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline':
                                                            {
                                                                borderColor:
                                                                    'rgba(131, 83, 226, 0.45)',
                                                            },
                                                    }}
                                                >
                                                    {options.map((roleValue) => (
                                                        <MenuItem
                                                            key={roleValue}
                                                            value={roleValue}
                                                            sx={{ fontSize: '13px' }}
                                                        >
                                                            {formatRoleLabel(roleValue)}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        );
                                    })()}
                                </LTItem>

                                <LTItem
                                    maxWidth="150px"
                                    minWidth="150px"
                                    style={{ marginRight: '1rem' }}
                                >
                                    {(() => {
                                        const status = (clientApp.status || '').toUpperCase();
                                        const isRegistered = status === 'REGISTERED';
                                        return (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                }}
                                            >
                                                {isRegistered ? (
                                                    <span
                                                        style={{
                                                            fontSize: '12px',
                                                            fontWeight: 500,
                                                            padding: '4px 10px',
                                                            borderRadius: '9999px',
                                                            backgroundColor:
                                                                'rgba(34, 197, 94, 0.12)',
                                                            color: '#15803d',
                                                        }}
                                                    >
                                                        {t('registered', {
                                                            lng: language,
                                                            defaultValue: 'Registered',
                                                        })}
                                                    </span>
                                                ) : (
                                                    <IconButton
                                                        size="small"
                                                        title={t('resend', {
                                                            lng: language,
                                                            defaultValue: 'Resend invitation',
                                                        })}
                                                        onClick={() =>
                                                            handleResendInvitation(clientApp.id)
                                                        }
                                                        sx={{
                                                            color: '#6d41d8',
                                                            '&:hover': {
                                                                backgroundColor:
                                                                    'rgba(131, 83, 226, 0.08)',
                                                            },
                                                        }}
                                                    >
                                                        <ForwardToInboxRoundedIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </LTItem>

                                <LTItem
                                    maxWidth="150px"
                                    minWidth="150px"
                                    style={{
                                        textAlign: 'center',
                                        marginRight: '1rem',
                                    }}
                                >
                                    {/* <div style={{ display: 'flex', justifyContent: 'center' }}> */}
                                    <Button
                                        border="purple"
                                        color="purple"
                                        onClick={() => handleSendResetLink(clientApp)}
                                    >
                                        {t('sendResetLink', { lng: language })}
                                    </Button>
                                    {/* </div> */}
                                </LTItem>
                                <LTItem
                                    maxWidth="120px"
                                    minWidth="120px"
                                    style={{
                                        textAlign: 'center',
                                        marginRight: '1rem',
                                    }}
                                >
                                    <IconButton
                                        onClick={() => setSelectedUserForPermissions(clientApp)}
                                        sx={{
                                            border: '1px solid rgba(131, 83, 226, 0.18)',
                                            borderRadius: '12px',
                                            color: '#6d41d8',
                                            backgroundColor: 'rgba(131, 83, 226, 0.08)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(131, 83, 226, 0.14)',
                                            },
                                        }}
                                    >
                                        <AdminPanelSettingsOutlinedIcon />
                                    </IconButton>
                                </LTItem>
                                {/* <LTItem
                                    maxWidth="150px"
                                    minWidth="150px"
                                    style={{
                                        textAlign: 'center',
                                        marginRight: '1rem',
                                    }}>
                                    <Button
                                        border="purple"
                                        color="purple"
                                        onClick={() => console.log('disabled')}
                                        disabled>
                                        {t('eligibility', { lng: language })}
                                    </Button>
                                </LTItem> */}

                                {/*SMALL SCREEN VERSION*/}

                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('clientId', { lng: language })}
                                >
                                    {clientApp.id}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('name', { lng: language })}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                        }}
                                    >
                                        {canEditUserProfile(role, clientApp.role) && (
                                            <IconButton
                                                size="small"
                                                title={t('editUser', {
                                                    lng: language,
                                                    defaultValue: 'Edit user',
                                                })}
                                                onClick={() => setSelectedUserForEdit(clientApp)}
                                                sx={{
                                                    padding: '2px',
                                                    color: '#6d41d8',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(131, 83, 226, 0.08)',
                                                    },
                                                }}
                                            >
                                                <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        )}
                                        <S.Link
                                            onClick={() =>
                                                dispatch(loadApp(clientApp.id.toString()))
                                            }
                                            to={`/dashboard/${clientApp.id}/client-profile`}
                                        >
                                            {`${clientApp.firstname} ${clientApp.lastname}`}
                                        </S.Link>
                                    </div>
                                </LTMobileItem>

                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('role', { lng: language, defaultValue: 'Role' })}
                                >
                                    {(() => {
                                        const canEdit = canEditUserRole(role, clientApp.role);
                                        const options = Array.from(
                                            new Set([
                                                ...assignableRoles,
                                                ...(clientApp.role ? [clientApp.role] : []),
                                            ])
                                        );
                                        if (!canEdit) {
                                            return (
                                                <span style={{ color: '#475569' }}>
                                                    {clientApp.role
                                                        ? formatRoleLabel(clientApp.role)
                                                        : '—'}
                                                </span>
                                            );
                                        }
                                        return (
                                            <FormControl
                                                size="small"
                                                fullWidth
                                                disabled={updatingRoleUserId === clientApp.id}
                                            >
                                                <Select
                                                    value={clientApp.role ?? ''}
                                                    onChange={(e) =>
                                                        handleRoleChange(
                                                            clientApp,
                                                            e.target.value as string
                                                        )
                                                    }
                                                    displayEmpty
                                                >
                                                    {options.map((roleValue) => (
                                                        <MenuItem key={roleValue} value={roleValue}>
                                                            {formatRoleLabel(roleValue)}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        );
                                    })()}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('status', { lng: language, defaultValue: 'Status' })}
                                >
                                    {(() => {
                                        const status = (clientApp.status || '').toUpperCase();
                                        const isRegistered = status === 'REGISTERED';
                                        return (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                }}
                                            >
                                                {isRegistered ? (
                                                    <span
                                                        style={{
                                                            fontSize: '12px',
                                                            fontWeight: 500,
                                                            padding: '4px 10px',
                                                            borderRadius: '9999px',
                                                            backgroundColor:
                                                                'rgba(34, 197, 94, 0.12)',
                                                            color: '#15803d',
                                                        }}
                                                    >
                                                        {t('registered', {
                                                            lng: language,
                                                            defaultValue: 'Registered',
                                                        })}
                                                    </span>
                                                ) : (
                                                    <button
                                                        title={t('resend', {
                                                            lng: language,
                                                            defaultValue: 'Resend invitation',
                                                        })}
                                                        onClick={() =>
                                                            handleResendInvitation(clientApp.id)
                                                        }
                                                    >
                                                        <ForwardToInboxRoundedIcon fill={'gray'} />
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('sendResetLink', { lng: language })}
                                >
                                    <button onClick={() => handleSendResetLink(clientApp)}>
                                        {' '}
                                        <ForwardToInboxRoundedIcon fill={'gray'} />
                                    </button>
                                </LTMobileItem>
                                <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('permissions', { lng: language })}
                                >
                                    <button
                                        onClick={() => setSelectedUserForPermissions(clientApp)}
                                    >
                                        <AdminPanelSettingsOutlinedIcon fill={'gray'} />
                                    </button>
                                </LTMobileItem>

                                {/* <LTMobileItem
                                    style={{ minWidth: '132px' }}
                                    name={t('eligibility', { lng: language })}>
                                    <button onClick={() => console.log('disabled')} disabled>
                                        {' '}
                                        <ForwardToInboxRoundedIcon fill={'gray'} />
                                    </button>
                                </LTMobileItem> */}
                            </CustomLTRow>
                        ))}
                    </>
                )}

                {isAdminUsersLoading &&
                    skeletonArray.map((_, idx) => (
                        <LTRow key={idx}>
                            <LTItem maxWidth="12%">
                                <Skeleton width={40} />
                            </LTItem>
                            <LTItem maxWidth="12%">
                                <Skeleton width={40} />
                            </LTItem>
                            <LTItem minWidth="28%">
                                <Skeleton width={115} />
                            </LTItem>
                            <LTItem minWidth="35%">
                                <Skeleton width={200} />
                            </LTItem>
                            <LTItem minWidth="28%">
                                <Skeleton width={115} />
                            </LTItem>
                            <LTItem minWidth="28%">
                                <Skeleton width={115} />
                            </LTItem>
                            <LTItem minWidth="28%">
                                <Skeleton width={115} />
                            </LTItem>
                            <LTItem minWidth="28%">
                                <Skeleton width={115} />
                            </LTItem>
                            <LTItem minWidth="14%">
                                <Skeleton width={79} />
                            </LTItem>
                            <LTItem minWidth="14%">
                                <Skeleton width={79} />
                            </LTItem>
                            <LTItem minWidth="14%">
                                <Skeleton width={79} />
                            </LTItem>
                            <LTItem minWidth="12%" lastItem>
                                <Skeleton width={79} />
                            </LTItem>
                        </LTRow>
                    ))}
            </S.ListTable>
        </>
    );
};

export default AdminTable;
